import cn from 'classnames';
import { forwardRef } from 'react';
import { toast as rhtToast } from 'react-hot-toast/headless';

import useSlots from '../../../../utils/hooks/use-slots';
import SlotsContext from '../../../../utils/slots-context';
import { CloseIcon } from '../../../foundations';
import { useToastContext } from '../toaster';

import { iconMapping, toastSlotNames } from './toast.utils';

import type { CSSProperties, ForwardedRef, ReactElement } from 'react';

import type { ToastProps } from './toast.types';

import './toast.scss';

/**
 * Toasts are non-modal, time-based window elements used to display short messages.
 * They usually appear at the top center of the screen and can disappear without a user’s action after a few seconds,
 * or after a user’s dismissal.
 *
 * ### Usage
 *
 * ```tsx
 * import { ActionsContainer, Button, Description, Toast, Toaster, toast } from '@bloomreach/react-banana-ui';
 *
 * function MyCustomComponent() {
 *   const showToast = (): void => {
 *     toast((t) => (
 *       <Toast variant='info'>
 *         This is toast heading
 *         <Description>Description for the toast</Description>
 *         <ActionsContainer>
 *           <Button onClick={() => toast.dismiss(t.id)} startIcon={<UndoIcon />} type="inline">
 *             Undo
 *           </Button>
 *         </ActionsContainer>
 *       </Toast>
 *     ));
 *   };
 *
 *   return (
 *     <div>
 *       <Toaster />
 *       <Button onClick={showToast} type="primary">Click me!</Button>
 *     </div>
 *   );
 * }
 * ```
 */
const Toast = forwardRef((props: ToastProps, forwardedRef: ForwardedRef<HTMLDivElement> | null): ReactElement => {
  const {
    children,
    className,
    dismissible = true,
    onDismiss,
    variant = 'info',
    ...rest // Capture any other properties to pass them to the root element, like `aria-*`, `data-*`, etc.
  } = props;

  const { contextValue: slotsContextValue, slots } = useSlots({ slotNames: toastSlotNames });
  const toastContextValue = useToastContext();

  const timerStyle = { '--rbui-toast-timer-duration': toastContextValue?.duration ?? Infinity } as CSSProperties;

  const handleClose = (): void => {
    rhtToast.dismiss(toastContextValue?.id);
    onDismiss?.();
  };

  return (
    <SlotsContext.Provider value={slotsContextValue}>
      <div {...rest} className={cn('rbui-toast', `rbui-toast--${variant}`, className)} ref={forwardedRef}>
        <div className="rbui-toast__icon">{iconMapping[variant]}</div>

        <div className="rbui-toast__body">
          <div className="rbui-toast__content">
            <div className="rbui-toast__title">{children}</div>
            <div className="rbui-toast__description">{slots.Description}</div>
          </div>

          <div className="rbui-toast__actions-container">{slots.ActionsContainer}</div>
        </div>

        {dismissible && (
          <button
            aria-label="Dismiss"
            className="rbui-toast__close-wrapper rbui-button-reset"
            onClick={handleClose}
            tabIndex={-1}
            type="button"
          >
            <CloseIcon className="rbui-toast__close-wrapper-icon" />
          </button>
        )}

        <div className="rbui-toast__timer" key={toastContextValue?.createdAt} style={timerStyle} />
      </div>
    </SlotsContext.Provider>
  );
});

Toast.displayName = 'Toast';

export default Toast;
